import { useEffect, useRef, useState } from "react";
import { BrowserRouter, Routes, Route, Outlet, Link, useLocation } from "react-router-dom";
import Lenis from "lenis";
import { motion } from "framer-motion";
import { Toaster } from "@/components/ui/sonner";
import { copy } from "./i18n";
import { Navbar } from "./components/Navbar";
import { Footer } from "./components/Footer";
import { Hero } from "./components/Hero";
import { Marquee } from "./components/Marquee";
import { Apartments } from "./components/Apartments";
import { Amenities } from "./components/Amenities";
import { History } from "./components/History";
import { Booking } from "./components/Booking";
import { Reviews } from "./components/Reviews";
import { Contact } from "./components/Contact";
import Admin from "./pages/Admin";
import Legal from "./pages/Legal";

const Welcome = ({ t }) => (
  <section data-testid="home-welcome" className="py-24 lg:py-28">
    <motion.div
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
      className="max-w-3xl mx-auto px-6 lg:px-10 text-center"
    >
      <p className="text-xs uppercase tracking-[0.35em] text-[#C29B62]">{t.home.overline}</p>
      <h2 className="mt-4 text-4xl sm:text-5xl font-light tracking-tight text-[#1C3829] font-serif-display">{t.home.title}</h2>
      <p className="mt-6 text-base text-[#4A4A4A] font-light leading-relaxed">{t.home.text}</p>
      <div className="mt-10 flex flex-wrap justify-center gap-4">
        <Link
          to="/appartamenti"
          data-testid="home-cta-apartments"
          className="bg-[#1C3829] text-[#F7F5F0] text-xs uppercase tracking-[0.25em] px-8 py-4 hover:text-[#C29B62] transition-colors"
        >
          {t.home.ctaApartments}
        </Link>
        <Link
          to="/prenota"
          data-testid="home-cta-booking"
          className="border border-[#1C3829]/40 text-[#1C3829] text-xs uppercase tracking-[0.25em] px-8 py-4 hover:border-[#C29B62] hover:text-[#C29B62] transition-colors"
        >
          {t.home.ctaBooking}
        </Link>
      </div>
    </motion.div>
  </section>
);

function Home({ t }) {
  return (
    <>
      <Hero t={t} />
      <Marquee text={t.marquee} />
      <Welcome t={t} />
      <Contact t={t} />
    </>
  );
}

function Shell({ lang, setLang }) {
  const t = copy[lang];
  const { pathname } = useLocation();
  const lenisRef = useRef(null);

  useEffect(() => {
    const lenis = new Lenis({ lerp: 0.09, smoothWheel: true });
    lenisRef.current = lenis;
    let raf;
    const loop = (time) => {
      lenis.raf(time);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
    };
  }, []);

  useEffect(() => {
    if (lenisRef.current) lenisRef.current.scrollTo(0, { immediate: true });
    window.scrollTo(0, 0);
  }, [pathname]);

  return (
    <div className="bg-[#F7F5F0] min-h-screen flex flex-col">
      <div className="noise-overlay" />
      <Navbar t={t} lang={lang} setLang={setLang} solid={pathname !== "/"} />
      <main className="flex-1">
        <Outlet />
      </main>
      <Footer t={t} />
    </div>
  );
}

function App() {
  const [lang, setLang] = useState(() => localStorage.getItem("vn_lang") || "it");
  const t = copy[lang];

  useEffect(() => {
    document.documentElement.lang = lang;
    localStorage.setItem("vn_lang", lang);
  }, [lang]);

  return (
    <BrowserRouter>
      <Routes>
        <Route element={<Shell lang={lang} setLang={setLang} />}>
          <Route path="/" element={<Home t={t} />} />
          <Route path="/appartamenti" element={<Apartments t={t} lang={lang} />} />
          <Route path="/servizi" element={<Amenities t={t} />} />
          <Route path="/prenota" element={<Booking t={t} lang={lang} />} />
          <Route path="/storia" element={<History t={t} />} />
          <Route path="/recensioni" element={<Reviews t={t} />} />
          <Route path="/contatti" element={<Contact t={t} />} />
        </Route>
        <Route path="/admin" element={<Admin />} />
        <Route path="/privacy" element={<Legal doc="privacy" />} />
        <Route path="/cookie-policy" element={<Legal doc="cookie" />} />
      </Routes>
      <Toaster position="bottom-right" />
    </BrowserRouter>
  );
}

export default App;
